import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { apiRequest } from '@/lib/queryClient';
import { Image, Upload, RefreshCw, AlertTriangle, ZoomIn, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

type PreviewStatus = 'no_artwork' | 'generating' | 'ready' | 'failed';

interface PlacementPreviewResponse {
  status: PreviewStatus;
  artworkPreviewUrl: string | null;
  mockupUrl: string | null;
  formatName?: string | null;
  dimensions?: string | null;
  templateAttribution?: string | null;
  error?: string | null;
}

interface VisualPlacementPreviewProps {
  campaignId: number;
  retailerId?: number;
  formatId?: number | null;
  campaignFormats?: Array<{ id: number; name: string }>;
  readOnly?: boolean;
}

const MVP_SUPPORTED_FORMAT_ID = 91;
const SMART_PLACEMENT_MAX_PDF_MB = 100;

function isMvpSupportedFormat(formatId: number | null | undefined): boolean {
  return Number(formatId) === MVP_SUPPORTED_FORMAT_ID;
}

function humanizeStatus(status: PreviewStatus | string | null | undefined): string {
  if (!status) return 'unknown';
  return String(status).replace(/_/g, ' ');
}

function getUploadErrorMessage(error: any): string {
  const code = error?.data?.code;
  const serverMessage = error?.data?.error || error?.message;
  if (code === 'LIMIT_FILE_SIZE') {
    return serverMessage || `PDF too large. Please upload a file under ${SMART_PLACEMENT_MAX_PDF_MB}MB.`;
  }
  if (typeof serverMessage === 'string' && serverMessage.includes('LIMIT_FILE_SIZE')) {
    return `PDF too large. Please upload a file under ${SMART_PLACEMENT_MAX_PDF_MB}MB.`;
  }
  return serverMessage || 'Failed to upload artwork PDF';
}

export function VisualPlacementPreview({
  campaignId,
  formatId,
  campaignFormats = [],
  readOnly = false,
}: VisualPlacementPreviewProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [lightbox, setLightbox] = useState<{ open: boolean; url: string; title: string }>({
    open: false,
    url: '',
    title: '',
  });
  const [generatingSince, setGeneratingSince] = useState<number | null>(null);
  const normalizedFormats = useMemo(() => {
    const seen = new Map<number, string>();
    for (const f of campaignFormats) {
      if (!f || !Number.isFinite(f.id)) continue;
      if (!seen.has(f.id)) seen.set(f.id, f.name || `Format ${f.id}`);
    }
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [campaignFormats]);
  const preferredFormatId = useMemo(() => {
    if (normalizedFormats.some((f) => f.id === MVP_SUPPORTED_FORMAT_ID)) return MVP_SUPPORTED_FORMAT_ID;
    if (formatId && normalizedFormats.some((f) => f.id === formatId)) return formatId;
    return normalizedFormats[0]?.id ?? formatId ?? null;
  }, [formatId, normalizedFormats]);
  const [selectedFormatId, setSelectedFormatId] = useState<number | null>(preferredFormatId);

  useEffect(() => {
    setSelectedFormatId((prev) => {
      if (prev && normalizedFormats.some((f) => f.id === prev)) return prev;
      return preferredFormatId;
    });
  }, [normalizedFormats, preferredFormatId]);

  const activeFormatId = selectedFormatId ?? formatId ?? null;
  const activeFormatMeta = normalizedFormats.find((f) => f.id === activeFormatId) ?? null;
  const activeSupported = isMvpSupportedFormat(activeFormatId);

  const queryKey = useMemo(
    () => ['/api/campaigns', campaignId, 'placement-preview', activeFormatId ?? 'none'],
    [campaignId, activeFormatId],
  );

  const previewQuery = useQuery<PlacementPreviewResponse>({
    queryKey,
    enabled: !!activeFormatId,
    queryFn: async () => {
      const res = await apiRequest(`/api/campaigns/${campaignId}/formats/${activeFormatId}/placement-preview`, 'GET');
      return res.json();
    },
    refetchInterval: (query) => {
      const data = query.state.data as PlacementPreviewResponse | undefined;
      if (data?.status !== 'generating') return false;
      if (generatingSince && Date.now() - generatingSince > 60_000) return false;
      return 2000;
    },
  });

  useEffect(() => {
    if (previewQuery.data?.status === 'generating') {
      setGeneratingSince((prev) => prev ?? Date.now());
      return;
    }
    setGeneratingSince(null);
  }, [previewQuery.data?.status]);

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiRequest(`/api/campaigns/${campaignId}/formats/${activeFormatId}/artwork`, 'POST', formData);
      return res.json();
    },
    onSuccess: async () => {
      setSelectedFile(null);
      setGeneratingSince(Date.now());
      await queryClient.invalidateQueries({ queryKey });
      toast({ title: 'Artwork uploaded', description: 'Generating visual placement preview…' });
    },
    onError: (error: any) => {
      toast({
        title: 'Upload failed',
        description: getUploadErrorMessage(error),
        variant: 'destructive',
      });
    },
  });

  const regenerateMutation = useMutation<any, any, boolean>({
    mutationFn: async (force: boolean) => {
      const url = `/api/campaigns/${campaignId}/formats/${activeFormatId}/mockup/regenerate${force ? '?force=1' : ''}`;
      const res = await apiRequest(url, 'POST');
      return res.json();
    },
    onSuccess: async () => {
      setGeneratingSince(Date.now());
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: any) => {
      toast({
        title: 'Regenerate failed',
        description: error?.message || 'Failed to regenerate mockup',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(`/api/campaigns/${campaignId}/formats/${activeFormatId}/artwork`, 'DELETE');
      return res.json();
    },
    onSuccess: async () => {
      setSelectedFile(null);
      setGeneratingSince(null);
      await queryClient.invalidateQueries({ queryKey });
      await queryClient.invalidateQueries({
        queryKey: ['/api/campaigns', campaignId, 'placement-preview-inventory', activeFormatId],
      });
      toast({ title: 'Artwork deleted', description: 'Placement preview has been reset for this format.' });
    },
    onError: (error: any) => {
      toast({
        title: 'Delete failed',
        description: error?.data?.error || error?.message || 'Failed to delete artwork',
        variant: 'destructive',
      });
    },
  });

  const inventoryQueries = useQueries({
    queries: normalizedFormats.map((f) => ({
      queryKey: ['/api/campaigns', campaignId, 'placement-preview-inventory', f.id],
      queryFn: async (): Promise<PlacementPreviewResponse> => {
        const res = await apiRequest(`/api/campaigns/${campaignId}/formats/${f.id}/placement-preview`, 'GET');
        return res.json();
      },
      staleTime: 15_000,
      refetchOnWindowFocus: false as const,
    })),
  });

  const inventoryRows = normalizedFormats.map((f, idx) => ({
    formatId: f.id,
    formatName: f.name,
    supported: isMvpSupportedFormat(f.id),
    status: (inventoryQueries[idx]?.data?.status ?? null) as PreviewStatus | null,
  }));

  const data = previewQuery.data;

  if (!activeFormatId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="h-5 w-5" />
            Visual Placement Preview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">
            Smart Placement Preview is enabled, but no campaign format could be detected for this campaign yet.
          </div>
        </CardContent>
      </Card>
    );
  }

  const status = data?.status ?? 'generating';
  const showTimedOutGenerating =
    status === 'generating' &&
    generatingSince !== null &&
    Date.now() - generatingSince > 60_000;

  const openLightbox = (url: string, title: string) => setLightbox({ open: true, url, title });

  return (
    <>
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Image className="h-5 w-5" />
                Visual Placement Preview
              </CardTitle>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="secondary">{activeFormatMeta?.name || data?.formatName || `Format ${activeFormatId}`}</Badge>
                <Badge variant={activeSupported ? 'default' : 'outline'}>
                  {activeSupported ? 'MVP supported' : 'Unsupported in MVP'}
                </Badge>
                <Badge variant="outline">{readOnly ? 'Read-only view' : 'Editable admin view'}</Badge>
                {data?.dimensions ? <span>{data.dimensions}</span> : null}
              </div>
            </div>
            <Badge variant={status === 'ready' ? 'default' : status === 'failed' ? 'destructive' : 'outline'}>
              {humanizeStatus(status)}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {normalizedFormats.length > 1 && (
            <div className="rounded-lg border bg-muted/20 p-3">
              <div className="mb-2 text-xs font-medium text-muted-foreground">
                Formats in this campaign ({normalizedFormats.length})
              </div>
              <div className="flex flex-wrap gap-2">
                {inventoryRows.map((row) => (
                  <button
                    key={row.formatId}
                    type="button"
                    onClick={() => setSelectedFormatId(row.formatId)}
                    className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                      row.formatId === activeFormatId
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-background text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    <span className="font-medium">{row.formatName}</span>
                    <span className="ml-2 opacity-80">
                      {row.supported ? 'supported' : 'unsupported'}
                      {row.status ? ` • ${humanizeStatus(row.status)}` : ''}
                    </span>
                  </button>
                ))}
              </div>
              <div className="mt-2 text-xs text-muted-foreground">
                Select a format to inspect its artwork/mockup status. MVP generation is enabled only for SuperValu Floor Graphic.
              </div>
            </div>
          )}

          {!activeSupported && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Smart Placement Preview MVP currently supports SuperValu Floor Graphic only. This campaign format can be viewed
                here, but upload/regeneration is disabled.
              </AlertDescription>
            </Alert>
          )}

          <div className="rounded-lg border bg-muted/10 p-3 text-sm">
            <div className="font-medium">Current target format</div>
            <div className="mt-1 text-muted-foreground">
              {activeFormatMeta?.name || data?.formatName || `Format ${activeFormatId}`} ({activeFormatId})
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              Status reflects the latest uploaded artwork for this campaign format.
            </div>
          </div>

          {!readOnly && activeSupported && (
            <div className="flex flex-wrap items-center gap-2">
              <Input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
                className="max-w-sm"
                disabled={uploadMutation.isPending}
              />
              <Button
                type="button"
                onClick={() => selectedFile && uploadMutation.mutate(selectedFile)}
                disabled={!selectedFile || uploadMutation.isPending}
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploadMutation.isPending ? 'Uploading…' : 'Upload PDF'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => regenerateMutation.mutate(false)}
                disabled={regenerateMutation.isPending || status === 'no_artwork'}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Regenerate
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => regenerateMutation.mutate(true)}
                disabled={regenerateMutation.isPending || status === 'no_artwork'}
              >
                Force rebuild
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  if (status === 'no_artwork') return;
                  const ok = window.confirm(
                    'Delete the uploaded artwork and generated mockup for this format? This resets the preview to no artwork.',
                  );
                  if (ok) deleteMutation.mutate();
                }}
                disabled={deleteMutation.isPending || status === 'no_artwork'}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {deleteMutation.isPending ? 'Deleting…' : 'Delete artwork'}
              </Button>
            </div>
          )}

          {!readOnly && !activeSupported && (
            <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
              Upload controls are hidden because the selected format is not supported in this MVP.
            </div>
          )}

          {status === 'no_artwork' && (
            <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
              {activeSupported
                ? 'Upload a PDF artwork file to generate a reference preview and in-store mockup.'
                : 'No artwork has been uploaded for this format.'}
            </div>
          )}

          {(status === 'generating' || status === 'ready' || status === 'failed') && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-xl border bg-white p-3">
                <div className="mb-2 text-xs font-medium text-muted-foreground">Artwork Reference</div>
                {status === 'generating' && !data?.artworkPreviewUrl ? (
                  <Skeleton className="h-56 w-full rounded-md" />
                ) : data?.artworkPreviewUrl ? (
                  <button
                    type="button"
                    className="group relative w-full overflow-hidden rounded-md border bg-white"
                    onClick={() => openLightbox(data.artworkPreviewUrl!, 'Artwork Reference')}
                  >
                    <img src={data.artworkPreviewUrl} alt="Artwork reference" className="h-auto w-full object-contain" />
                    <div className="absolute right-2 top-2 rounded-full bg-white/90 p-1 shadow">
                      <ZoomIn className="h-4 w-4 text-gray-700" />
                    </div>
                  </button>
                ) : (
                  <div className="h-56 rounded-md border border-dashed bg-muted/20" />
                )}
              </div>

              <div className="rounded-xl border bg-muted/20 p-3">
                <div className="mb-2 text-xs font-medium text-muted-foreground">In-Store Preview</div>
                {status === 'ready' && data?.mockupUrl ? (
                  <button
                    type="button"
                    className="group relative w-full overflow-hidden rounded-md border bg-white transition-opacity duration-300"
                    onClick={() => openLightbox(data.mockupUrl!, 'In-Store Preview')}
                  >
                    <img src={data.mockupUrl} alt="In-store preview mockup" className="h-auto w-full object-contain bg-white" />
                    <div className="absolute right-2 top-2 rounded-full bg-white/90 p-1 shadow">
                      <ZoomIn className="h-4 w-4 text-gray-700" />
                    </div>
                  </button>
                ) : status === 'generating' ? (
                  <div className="space-y-2">
                    <Skeleton className="h-56 w-full rounded-md" />
                    <div className="text-xs text-muted-foreground">Generating preview…</div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="h-56 rounded-md border border-dashed bg-muted/30" />
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>Preview temporarily unavailable.</AlertDescription>
                    </Alert>
                  </div>
                )}
              </div>
            </div>
          )}

          {status === 'failed' && data?.error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{data.error}</AlertDescription>
            </Alert>
          )}

          {showTimedOutGenerating && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Still generating. You can refresh or try regenerate again.
              </AlertDescription>
            </Alert>
          )}

          {data?.templateAttribution ? (
            <div className="text-xs text-muted-foreground">{data.templateAttribution}</div>
          ) : null}
          <div className="text-xs text-muted-foreground">Automatically generated from approved artwork.</div>
        </CardContent>
      </Card>

      <Dialog open={lightbox.open} onOpenChange={(open) => setLightbox((prev) => ({ ...prev, open }))}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>{lightbox.title}</DialogTitle>
          </DialogHeader>
          {lightbox.url ? (
            <img src={lightbox.url} alt={lightbox.title} className="max-h-[75vh] w-full rounded-md object-contain" />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default VisualPlacementPreview;
